import React, { useEffect, useMemo, useState } from "react";

const AUTH_URL = "https://mcp.obto.co/ms/v2/auth.bto";
const DEMO_URL = "https://obto-ops-control-room-staging.obto.co/";
const DESKTOP_CAPTURE = "https://staging.obto.co/ms/filereader.bto?n=mtjavbqr914fdf61_staging_preview-captures_preview-1788305412915.png";

const buildDestination = () => {
  const target = new URL(AUTH_URL);
  const current = new URLSearchParams(window.location.search);
  ["utm_source", "utm_medium", "utm_campaign", "utm_content", "utm_term", "utm_audience"].forEach((key) => {
    const value = current.get(key);
    if (value) target.searchParams.set(key, value.slice(0, 100));
  });
  target.searchParams.set("source_page", "obto-mcp-platform");
  return target.toString();
};

const proofItems = [
  ["Built through MCP", "A frontier AI used a tenant-bound OBTO MCP endpoint and governed tools to scaffold, build, publish, and verify this staging proof."],
  ["Policy is visible", "The workflow applies transparent rules under onboarding-readiness-v1.0."],
  ["Authority is named", "Approval-bound work stops for Maya Chen, the synthetic Operations Lead."],
  ["State is persisted", "The approved synthetic state survives a full browser refresh."],
  ["Outcome is verified", "The application reads the persisted result back before calling the step complete."],
  ["History is ordered", "Nine audit events preserve the path from trigger through verified outcome."]
];

const roles = [
  ["Founders and owners", "Replace spreadsheet, inbox, and point-tool glue with one governed workflow worth verifying."],
  ["Operations leaders", "Turn repeated exceptions into explicit evidence, policy, approval, persistence, and audit."],
  ["Agencies and fractional CTOs", "Deliver client workflows on reusable infrastructure without hiding the operating boundary."]
];

const faq = [
  ["Is OBTO the Operations Control Room?", "No. OBTO is the MCP platform. The Operations Control Room is one synthetic, verified workflow built on it as evidence—not the product or target industry."],
  ["Was the application actually built on OBTO?", "Yes. The staging application, API routes, persisted state, approval action, refresh survival, and audit sequence were verified. The evidence ledger limits every claim to what was observed."],
  ["Does OBTO remove human approval?", "No. The demonstrated workflow prepares a bounded action and stops at a named approval boundary before applying the state change."],
  ["Does the campaign use customer data?", "No. The demonstration contains synthetic data only. This landing page has no normal-visitor analytics or external advertising tag enabled."],
  ["What is the next step?", "Connect a tenant-bound OBTO MCP endpoint to a supported frontier AI client and start with one thin workflow whose outcome can be verified."]
];

export default function ControlRoomCampaign() {
  const [openFaq, setOpenFaq] = useState(-1);
  const authUrl = useMemo(buildDestination, []);

  useEffect(() => {
    document.title = "OBTO MCP Platform | Build, Deploy, Integrate, Verify, Operate";
  }, []);

  return (
    <main className="cr-page">
      <style>{`
        :root{color-scheme:dark}
        *{box-sizing:border-box}
        html{scroll-behavior:smooth}
        body{margin:0;background:#121214}
        .cr-page{--bg:#121214;--panel:#1E1E22;--panel2:#26262B;--line:#2E2E34;--mint:#B4AAFF;--gold:#B4AAFF;--text:#F5F3EF;--muted:#9A98A3;background:var(--bg);color:var(--text);font-family:"Figtree",system-ui,sans-serif;line-height:1.55;min-height:100vh}
        .cr-page a{color:inherit}
        .cr-wrap{width:min(1180px,calc(100% - 40px));margin:auto}
        .cr-nav{height:72px;display:flex;align-items:center;justify-content:space-between;border-bottom:1px solid var(--line)}
        .cr-logo{font-weight:850;letter-spacing:.16em;text-decoration:none}
        .cr-button{display:inline-flex;align-items:center;justify-content:center;padding:14px 22px;border-radius:999px;background:#5345C9;border:1px solid #5345C9;color:#FFFFFF!important;font-weight:800;text-decoration:none;transition:.2s}
        .cr-button:hover{transform:translateY(-2px);filter:brightness(1.04)}
        .cr-button.secondary{background:transparent;color:var(--text)!important;border-color:var(--line)}
        .cr-hero{padding:84px 0 58px;text-align:center}
        .cr-kicker{color:var(--mint);font-size:12px;letter-spacing:.18em;font-weight:850}
        .cr-hero h1{font-size:clamp(44px,6.3vw,78px);line-height:1.02;letter-spacing:-.047em;max-width:1080px;margin:18px auto 24px}
        .cr-lede{font-size:clamp(18px,2vw,22px);color:var(--muted);max-width:850px;margin:0 auto}
        .cr-actions{display:flex;gap:12px;justify-content:center;flex-wrap:wrap;margin:32px 0 20px}
        .cr-trust{color:var(--gold);font-size:13px;letter-spacing:.06em}
        .cr-proof-shell{margin:38px auto 0;padding:12px;border-radius:26px;background:#1E1E22;border:1px solid var(--line);box-shadow:0 34px 100px rgba(0,0,0,.4);max-width:1120px}
        .cr-proof-shell img{display:block;width:100%;height:auto;border-radius:17px;background:#121214}
        .cr-caption{display:flex;justify-content:space-between;gap:20px;padding:13px 8px 3px;color:var(--muted);font-size:12px;text-align:left}
        .cr-section{padding:82px 0;border-top:1px solid rgba(46,46,52,.68)}
        .cr-section h2{font-size:clamp(34px,4.4vw,56px);line-height:1.06;letter-spacing:-.038em;margin:14px 0 18px;max-width:900px}
        .cr-sub{color:var(--muted);font-size:18px;max-width:800px}
        .cr-grid{display:grid;grid-template-columns:repeat(3,1fr);gap:18px;margin-top:36px}
        .cr-card{background:var(--panel);border:1px solid var(--line);border-radius:20px;padding:25px}
        .cr-card .cr-label{font-size:11px;color:var(--gold);font-weight:850;letter-spacing:.13em}
        .cr-card h3{font-size:21px;line-height:1.24;margin:9px 0 12px}
        .cr-card p{color:var(--muted);margin:0}
        .cr-flow{display:grid;grid-template-columns:repeat(5,1fr);gap:10px;margin-top:34px}
        .cr-flow div{background:var(--panel2);border:1px solid var(--line);border-radius:15px;padding:19px 12px;text-align:center;font-weight:750;position:relative}
        .cr-disclosure{display:grid;grid-template-columns:1fr 1fr;gap:18px;margin-top:34px}
        .cr-list{background:var(--panel);border:1px solid var(--line);border-radius:20px;padding:26px}
        .cr-list h3{margin:0 0 15px}
        .cr-list ul{margin:0;padding-left:20px;color:var(--muted)}
        .cr-list li+li{margin-top:10px}
        .cr-faq{margin-top:32px}
        .cr-faq button{width:100%;display:flex;justify-content:space-between;gap:20px;text-align:left;background:transparent;color:var(--text);border:0;border-top:1px solid var(--line);padding:22px 0;font:inherit;font-weight:760;cursor:pointer}
        .cr-answer{color:var(--muted);padding:0 0 22px;max-width:820px}
        .cr-final{text-align:center;padding:96px 20px;background:none}
        .cr-final h2{font-size:clamp(36px,5vw,60px);line-height:1.05;letter-spacing:-.04em;max-width:840px;margin:14px auto 24px}
        .cr-note{font-size:12px;color:var(--muted);margin-top:18px}
        @media(max-width:900px){.cr-grid{grid-template-columns:1fr 1fr}.cr-flow{grid-template-columns:1fr 1fr}.cr-disclosure{grid-template-columns:1fr}.cr-caption{display:block}.cr-caption span{display:block;margin-top:4px}}
        @media(max-width:600px){.cr-wrap{width:min(100% - 24px,1180px)}.cr-nav .cr-button{padding:10px 13px;font-size:13px}.cr-hero{padding-top:58px}.cr-hero h1{font-size:44px}.cr-actions{display:grid}.cr-button{width:100%}.cr-grid{grid-template-columns:1fr}.cr-flow{grid-template-columns:1fr 1fr}.cr-section{padding:62px 0}.cr-proof-shell{padding:7px;border-radius:18px}.cr-proof-shell img{border-radius:12px}}
      `}</style>

      <div className="cr-wrap">
        <nav className="cr-nav" aria-label="Campaign navigation">
          <a className="cr-logo" href="/" aria-label="OBTO home"><img src="https://www.obto.co/ms/filereader.bto?n=mush6zo8f1132076_www_brand_obto-lockup-reverse.svg" alt="OBTO" width={112} height={26} style={{ display: "block", height: "26px", width: "auto" }} /></a>
          <a className="cr-button" href={authUrl}>Get your MCP endpoint</a>
        </nav>

        <section className="cr-hero">
          <div className="cr-kicker">OBTO MCP PLATFORM</div>
          <h1>Give frontier AI an MCP platform for real applications and business workflows.</h1>
          <p className="cr-lede">Connect a supported frontier AI client to a tenant-bound OBTO MCP endpoint. Use governed tools to build, deploy, integrate, monitor, and operate applications—with explicit boundaries and verification.</p>
          <div className="cr-actions">
            <a className="cr-button" href={authUrl}>Get your OBTO MCP endpoint</a>
            <a className="cr-button secondary" href={DEMO_URL} target="_blank" rel="noopener noreferrer">See one verified workflow proof</a>
          </div>
          <div className="cr-trust">Built through MCP. Operated with boundaries. Verified on OBTO.</div>
          <figure className="cr-proof-shell">
            <img src={DESKTOP_CAPTURE} alt="One verified workflow proof built on the OBTO MCP platform, using synthetic evidence, policy, approval, persistence, and audit history" />
            <figcaption className="cr-caption"><strong>OBTO platform proof</strong><span>Operations Control Room is one synthetic example · not the product or target industry</span></figcaption>
          </figure>
        </section>
      </div>

      <section className="cr-section" id="proof">
        <div className="cr-wrap">
          <div className="cr-kicker">WHAT WAS VERIFIED</div>
          <h2>One verified example of what the OBTO MCP platform can build and operate.</h2>
          <p className="cr-sub">The Control Room is evidence. OBTO and its MCP capabilities are the product.</p>
          <div className="cr-grid">
            {proofItems.map(([title, body], index) => <article className="cr-card" key={title}>
              <div className="cr-label">{String(index + 1).padStart(2, "0")}</div>
              <h3>{title}</h3>
              <p>{body}</p>
            </article>)}
          </div>
        </div>
      </section>

      <section className="cr-section">
        <div className="cr-wrap">
          <div className="cr-kicker">THE OBTO PLATFORM PATH</div>
          <h2>Connect through MCP. Build, deploy, integrate, operate, and verify.</h2>
          <div className="cr-flow">{["MCP-connected AI", "Build + deploy", "Integrate systems", "Apply policy + authority", "Monitor + verify"].map((step) => <div key={step}>{step}</div>)}</div>
        </div>
      </section>

      <section className="cr-section">
        <div className="cr-wrap">
          <div className="cr-kicker">WHO THIS IS FOR</div>
          <h2>Start with one workflow whose outcome matters enough to verify.</h2>
          <div className="cr-grid">{roles.map(([title, body]) => <article className="cr-card" key={title}><div className="cr-label">OBTO PLATFORM</div><h3>{title}</h3><p>{body}</p></article>)}</div>
        </div>
      </section>

      <section className="cr-section">
        <div className="cr-wrap">
          <div className="cr-kicker">CLAIM BOUNDARY</div>
          <h2>Trust begins with saying exactly what the proof does—and does not—show.</h2>
          <div className="cr-disclosure">
            <div className="cr-list">
              <h3>Verified here</h3>
              <ul>
                <li>OBTO MCP build, publish, and application validation.</li>
                <li>Synthetic persistence and transparent versioned policy.</li>
                <li>Named approval, persisted verification, refresh survival, and ordered audit history.</li>
                <li>Clean desktop and mobile browser verification.</li>
              </ul>
            </div>
            <div className="cr-list">
              <h3>Not claimed</h3>
              <ul>
                <li>Production customer results, revenue, time saved, or performance gains.</li>
                <li>Human-free autonomy, continuous monitoring, or external provider execution.</li>
                <li>Compliance, security, or reliability outcomes beyond the observed staging proof.</li>
                <li>That the synthetic onboarding scenario is the OBTO product category.</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      <section className="cr-section">
        <div className="cr-wrap">
          <div className="cr-kicker">QUESTIONS</div>
          <h2>Make the platform and the boundary clear.</h2>
          <div className="cr-faq">{faq.map(([question, answer], index) => <div key={question}>
            <button onClick={() => setOpenFaq(openFaq === index ? -1 : index)} aria-expanded={openFaq === index}><span>{question}</span><span>{openFaq === index ? "−" : "+"}</span></button>
            {openFaq === index && <div className="cr-answer">{answer}</div>}
          </div>)}</div>
        </div>
      </section>

      <section className="cr-final">
        <div className="cr-kicker">FROM PROMPT TO OPERATION ON OBTO</div>
        <h2>Connect through MCP. Build and operate on OBTO.</h2>
        <a className="cr-button" href={authUrl}>Get your OBTO MCP endpoint</a>
        <div className="cr-note">Campaign preparation surface. Normal visitor analytics and external advertising tags are not active.</div>
      </section>
    </main>
  );
}
